import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import PrivacyConsentDocument
  from './PrivacyConsentDocument';

import './PrivacyPolicyModal.css';


const KOREAN_DEFAULT_VERSION =
  '1.0';


const KOREAN_PRIVACY_CONSENT_VERSIONS =
  Object.freeze([
    {
      version:
        '1.0',

      label:
        'V1.0',

      source:
        'file',

      fileName:
        'PrivacyConsentForm_V1.0.json',
    },
  ]);


function getPrivacyConsentFilePath(
  fileName,
) {
  const publicUrl =
    String(
      process.env.PUBLIC_URL ||
      '',
    ).replace(
      /\/+$/,
      '',
    );


  return `${publicUrl}/PrivacyConsent/${fileName}`;
}


function getLocalizedPrivacyConsentFileName(
  fileName,
  language,
) {
  if (
    language ===
    'ko'
  ) {
    return fileName;
  }


  return fileName.replace(
    /\.json$/i,
    `_${language}.json`,
  );
}


function getCurrentLanguage(
  i18n,
) {
  return String(
    i18n.resolvedLanguage ||
    i18n.language ||
    'ko',
  )
    .trim()
    .toLowerCase()
    .replace(
      /_/g,
      '-',
    )
    .split(
      '-',
    )[0];
}


export default function PrivacyPolicyModal({
  open = false,
  onClose = () => {},
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      'app',
    );


  const closeButtonRef =
    useRef(
      null,
    );


  const currentLanguage =
    getCurrentLanguage(
      i18n,
    );


  const [
    selectedVersion,
    setSelectedVersion,
  ] =
    useState(
      KOREAN_DEFAULT_VERSION,
    );


  const [
    documentData,
    setDocumentData,
  ] =
    useState(
      null,
    );


  const [
    isLoading,
    setIsLoading,
  ] =
    useState(
      false,
    );


  const [
    loadError,
    setLoadError,
  ] =
    useState(
      '',
    );


  const [
    reloadCount,
    setReloadCount,
  ] =
    useState(
      0,
    );


  const selectedVersionEntry =
    useMemo(
      () => {
        const versionEntry =
          KOREAN_PRIVACY_CONSENT_VERSIONS.find(
            (
              entry,
            ) =>
              entry.version ===
              selectedVersion,
          ) ||
          KOREAN_PRIVACY_CONSENT_VERSIONS[0];


        return {
          ...versionEntry,

          fileName:
            getLocalizedPrivacyConsentFileName(
              versionEntry.fileName,
              currentLanguage,
            ),
        };
      },
      [
        currentLanguage,
        selectedVersion,
      ],
    );


  const isFileDocument =
    selectedVersionEntry?.source ===
    'file';


  useEffect(
    () => {
      if (
        !open
      ) {
        return;
      }


      setSelectedVersion(
        KOREAN_DEFAULT_VERSION,
      );
    },
    [
      currentLanguage,
      open,
    ],
  );


  useEffect(
    () => {
      if (
        !open
      ) {
        return undefined;
      }


      const previousBodyOverflow =
        document.body.style.overflow;


      document.body.style.overflow =
        'hidden';


      const handleKeyDown =
        (
          event,
        ) => {
          if (
            event.key ===
            'Escape'
          ) {
            onClose();
          }
        };


      window.addEventListener(
        'keydown',
        handleKeyDown,
      );


      window.requestAnimationFrame(
        () => {
          closeButtonRef.current?.focus();
        },
      );


      return () => {
        document.body.style.overflow =
          previousBodyOverflow;


        window.removeEventListener(
          'keydown',
          handleKeyDown,
        );
      };
    },
    [
      onClose,
      open,
    ],
  );


  useEffect(
    () => {
      if (
        !open ||
        !isFileDocument ||
        !selectedVersionEntry?.fileName
      ) {
        setDocumentData(
          null,
        );

        setLoadError(
          '',
        );

        setIsLoading(
          false,
        );

        return undefined;
      }


      const abortController =
        new AbortController();


      const loadPrivacyConsentDocument =
        async () => {
          setIsLoading(
            true,
          );

          setLoadError(
            '',
          );

          setDocumentData(
            null,
          );


          try {
            const filePath =
              getPrivacyConsentFilePath(
                selectedVersionEntry.fileName,
              );


            const response =
              await fetch(
                filePath,
                {
                  signal:
                    abortController.signal,

                  headers: {
                    Accept:
                      'application/json',
                  },
                },
              );


            if (
              !response.ok
            ) {
              throw new Error(
                `Fail to load privacy policy file: ${response.status}`,
              );
            }


            const responseData =
              await response.json();


            setDocumentData(
              responseData,
            );
          } catch (
            error
          ) {
            if (
              error?.name ===
              'AbortError'
            ) {
              return;
            }


            console.error(
              '[PrivacyPolicyModal] Fail to load privacy policy file.',
              error,
            );


            setLoadError(
              t(
                'privacyPolicy.loadFailed',
              ),
            );
          } finally {
            if (
              !abortController.signal.aborted
            ) {
              setIsLoading(
                false,
              );
            }
          }
        };


      loadPrivacyConsentDocument();


      return () => {
        abortController.abort();
      };
    },
    [
      isFileDocument,
      open,
      reloadCount,
      selectedVersionEntry,
      t,
    ],
  );


  if (
    !open
  ) {
    return null;
  }


  const handleBackdropMouseDown =
    (
      event,
    ) => {
      if (
        event.target ===
        event.currentTarget
      ) {
        onClose();
      }
    };


  const handleRetry =
    () => {
      setReloadCount(
        (
          previousCount,
        ) =>
          previousCount +
          1,
      );
    };


  return (
    <div
      className="privacy-policy-backdrop"
      onMouseDown={
        handleBackdropMouseDown
      }
      role="presentation"
    >
      <section
        className="privacy-policy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-policy-modal-title"
      >
        <header
          className="privacy-policy-header"
        >
          <div
            className="privacy-policy-title-group"
          >
            <span
              className="privacy-policy-title-icon"
            >
              !
            </span>

            <div>
              <h2
                id="privacy-policy-modal-title"
              >
                {t(
                  'signup.agreement.privacyPolicy',
                )}
              </h2>

              <p>
                {t(
                  'privacyPolicy.description',
                )}
              </p>
            </div>
          </div>

          <button
            ref={
              closeButtonRef
            }
            type="button"
            className="privacy-policy-close-button"
            onClick={
              onClose
            }
            aria-label={
              t(
                'privacyPolicy.close',
              )
            }
          >
            ×
          </button>
        </header>

        <div
          className="privacy-policy-toolbar"
        >
          <label
            htmlFor="privacyPolicyVersion"
          >
            {t(
              'privacyPolicy.version',
            )}
          </label>

          <select
            id="privacyPolicyVersion"
            value={
              selectedVersion
            }
            onChange={
              (
                event,
              ) =>
                setSelectedVersion(
                  event.target.value,
                )
            }
          >
            {KOREAN_PRIVACY_CONSENT_VERSIONS.map(
              (
                entry,
              ) => (
                <option
                  key={
                    entry.version
                  }
                  value={
                    entry.version
                  }
                >
                  {
                    entry.label
                  }
                </option>
              ),
            )}
          </select>
        </div>

        <div
          className="privacy-policy-document-container"
          aria-busy={
            isLoading
          }
        >
          {isFileDocument && (
            <>
              {isLoading && (
                <div
                  className="privacy-policy-state"
                >
                  <span
                    className="privacy-policy-spinner"
                  />

                  <p>
                    {t(
                      'privacyPolicy.loading',
                    )}
                  </p>
                </div>
              )}

              {!isLoading &&
                loadError && (
                  <div
                    className="privacy-policy-state is-error"
                  >
                    <p>
                      {
                        loadError
                      }
                    </p>

                    <button
                      type="button"
                      onClick={
                        handleRetry
                      }
                    >
                      {t(
                        'privacyPolicy.retry',
                      )}
                    </button>
                  </div>
                )}

              {!isLoading &&
                !loadError &&
                documentData && (
                  <PrivacyConsentDocument
                    documentData={
                      documentData
                    }
                  />
                )}

              {!isLoading &&
                !loadError &&
                !documentData && (
                  <div
                    className="privacy-policy-state"
                  >
                    <p>
                      {t(
                        'privacyPolicy.empty',
                      )}
                    </p>
                  </div>
                )}
            </>
          )}

        </div>

        <footer
          className="privacy-policy-footer"
        >
          <span>
            {t(
              'privacyPolicy.footerNotice',
            )}
          </span>

          <button
            type="button"
            onClick={
              onClose
            }
          >
            {t(
              'privacyPolicy.close',
            )}
          </button>
        </footer>
      </section>
    </div>
  );
}
